<template>
  <!-- One entry of an activity log, the same in every list (US 9438) -->
  <div class="ae" :class="{'ae--compact': compact}" data-qid="activity-entry">
    <span class="ae__avatar"
          :class="'is-' + tone"
          v-b-tooltip.hover
          :title="who">
      <font-awesome-icon v-if="!entry.causer" icon="cog"/>
      <template v-else>{{ initials }}</template>
      <span class="ae__badge" :class="'ae__badge--' + entry.action"><font-awesome-icon :icon="badgeIcon"/></span>
    </span>

    <div class="ae__body">
      <div class="ae__line">
        <!-- An explicit space: the template compiler drops a plain one before the v-if -->
        <b class="ae__who">{{ who }}</b>{{ ' ' }}<span v-if="entry.sentence.text">{{ entry.sentence.text }}</span><i18n v-else :path="sentencePath" tag="span">
          <span place="item"><ActivityItemChip v-if="entry.sentence.item" :item="entry.sentence.item" :here="here"/></span>
          <span place="parent"><ActivityItemChip v-if="entry.sentence.parent" :item="entry.sentence.parent" :here="here"/></span>
          <b place="count">{{ count }}</b>
          <span v-for="(value, key) in entry.sentence.values" :key="'value-' + key" :place="key">{{ value }}</span>
          <span v-if="entry.sentence.pill"
                :place="entry.sentence.pill.place"
                class="ae-pill"
                :class="'ae-pill--' + entry.sentence.pill.tone"
          >{{ entry.sentence.pill.text }}</span>
        </i18n>
        <span v-if="entry.pill" class="ae-pill" :class="'ae-pill--' + entry.pill.tone">{{ entry.pill.text }}</span>
      </div>
      <div v-if="entry.context && !hideContext" class="ae__context">{{ entry.context }}</div>
      <div v-for="(note, index) in entry.notes" :key="'note-' + index" class="ae__note">{{ note }}</div>
      <div v-if="entry.comment" class="ae__comment"><font-awesome-icon icon="comment" class="mr-1"/>{{ entry.comment }}</div>

      <!-- Compact: one line of changes, the whole list on click -->
      <div v-if="changes.length && compact && !open" class="ae-chips">
        <span v-for="change in changes.slice(0, chipCount)" :key="change.key" class="ae-chip">
          <span class="ae-chip__key">{{ change.label }}</span>
          <template v-if="change.kind === 'value' && change.to !== null">
            <span class="ae-arrow">→</span><b>{{ change.to }}</b>
          </template>
          <template v-else-if="change.kind === 'list'">
            <span v-if="change.added.length" class="ae-add">+{{ change.added.join(', ') }}</span>
            <span v-if="change.removed.length" class="ae-remove">{{ change.removed.join(', ') }}</span>
          </template>
        </span>
        <span v-if="changes.length > chipCount"
              class="ae-more"
              @click="open = true"
              data-qid="activity-entry-more">
          {{ $tc('activity_log.more_changes', changes.length - chipCount, { count: changes.length - chipCount }) }}
        </span>
        <span v-else-if="hasText" class="ae-more" @click="open = true">{{ $t('activity_log.show') }}</span>
      </div>

      <!-- Comfortable: from → to for each changed field -->
      <div v-if="changes.length && (!compact || open)" class="ae-diff" data-qid="activity-entry-changes">
        <div v-for="change in shownChanges" :key="change.key" class="ae-diff__row">
          <span class="ae-diff__field">{{ change.label }}</span>
          <span class="ae-diff__value">
            <template v-if="change.kind === 'value'">
              <span v-if="change.hadFrom !== false || change.from !== null" :class="change.from === null ? 'ae-empty' : 'ae-old'">{{ change.from === null ? $t('activity_log.empty') : change.from }}</span>
              <span v-if="change.hadFrom !== false || change.from !== null" class="ae-arrow">→</span>
              <span :class="change.to === null ? 'ae-empty' : 'ae-new'">{{ change.to === null ? $t('activity_log.empty') : change.to }}</span>
            </template>
            <template v-else-if="change.kind === 'list'">
              <span v-for="name in change.added" :key="'a-' + name" class="ae-add">+ {{ name }}</span>
              <span v-for="name in change.removed" :key="'r-' + name" class="ae-remove">{{ name }}</span>
            </template>
            <template v-else-if="change.kind === 'text'">
              <span class="ae-new">{{ $t('activity_log.edited') }}</span>
              <span class="ae-more" @click="toggleText(change.key)">
                {{ openTexts.indexOf(change.key) === -1 ? $t('activity_log.show') : $t('activity_log.hide') }}
                <font-awesome-icon :icon="openTexts.indexOf(change.key) === -1 ? 'chevron-down' : 'chevron-up'"/>
              </span>
              <span v-if="openTexts.indexOf(change.key) !== -1" class="ae-text" data-qid="activity-entry-text"><span
                v-for="(part, index) in change.parts"
                :key="index"
                :class="'ae-text__' + part.kind"
              >{{ part.text }}</span></span>
            </template>
            <span v-else class="ae-new">{{ $t('activity_log.edited') }}</span>
          </span>
        </div>
        <div v-if="changes.length > shownChanges.length || allChanges" class="ae-diff__row ae-diff__row--more">
          <span class="ae-diff__field"></span>
          <span class="ae-more" @click="allChanges = !allChanges">
            {{ allChanges ? $t('activity_log.show_fewer') : $tc('activity_log.more_changes', changes.length - shownChanges.length, { count: changes.length - shownChanges.length }) }}
            <font-awesome-icon :icon="allChanges ? 'chevron-up' : 'chevron-down'"/>
          </span>
        </div>
      </div>

      <!-- Created: the values it was created with, on request -->
      <template v-if="entry.values.length && !compact">
        <span class="ae-more ae-more--values" @click="showValues = !showValues" data-qid="activity-entry-values">
          {{ showValues ? $t('activity_log.hide_values') : $t('activity_log.show_values') }}
          <font-awesome-icon :icon="showValues ? 'chevron-up' : 'chevron-down'"/>
        </span>
        <div v-if="showValues" class="ae-diff">
          <div v-for="row in entry.values" :key="row.key" class="ae-diff__row">
            <span class="ae-diff__field">{{ row.label }}</span>
            <span class="ae-diff__value"><span class="ae-new">{{ row.value }}</span></span>
          </div>
        </div>
      </template>
    </div>

    <span class="ae__time" v-b-tooltip.hover :title="fullTime">{{ time }}</span>
  </div>
</template>

<script>
import moment from 'moment';
import ActivityItemChip from './ActivityItemChip.vue';

const BADGES = {
  created: 'plus',
  updated: 'pen',
  deleted: 'trash-alt',
  linked: 'link',
  unlinked: 'unlink',
  status: 'flag',
  signed: 'check',
  uploaded: 'cloud-upload-alt'
};

const TONES = ['teal', 'blue', 'violet', 'amber', 'rose'];

export default {
  name: 'ActivityEntry',
  components: {
    ActivityItemChip
  },
  props: {
    // From describeActivity(), or a burst of them merged by the feed
    entry: {
      type: Object,
      required: true
    },
    // Panels: changes as one line of chips
    compact: {
      type: Boolean,
      default: false
    },
    // The item the list belongs to: no link to the page you're on
    here: {
      type: Object,
      default: null
    },
    hideContext: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      open: false,
      allChanges: false,
      showValues: false,
      openTexts: []
    };
  },
  computed: {
    who() {
      return this.entry.causer ? this.entry.causer.name : this.$t('activity_log.system');
    },
    initials() {
      const parts = (this.who || '').trim().split(/\s+/);
      return ((parts[0] || '').charAt(0) + (parts.length > 1 ? parts[parts.length - 1].charAt(0) : '')).toUpperCase();
    },
    // Summaries only carry the name, so the colour comes from the name there
    tone() {
      const causer = this.entry.causer;
      if (!causer) {
        return 'system';
      }
      const seed = causer.id !== undefined ? Number(causer.id) : (causer.name || '').split('').reduce((sum, letter) => sum + letter.charCodeAt(0), 0);
      return TONES[seed % TONES.length];
    },
    badgeIcon() {
      return BADGES[this.entry.action] || 'pen';
    },
    changes() {
      return this.entry.changes || [];
    },
    // Several saves merged: "made 5 changes to …"
    count() {
      return this.changes.length;
    },
    sentencePath() {
      return this.entry.saves > 1 && this.changes.length > 1 ? 'activity_log.sentence.burst' : this.entry.sentence.path;
    },
    chipCount() {
      return 3;
    },
    hasText() {
      return this.changes.some(change => change.kind === 'text');
    },
    shownChanges() {
      return this.allChanges ? this.changes : this.changes.slice(0, 4);
    },
    time() {
      return moment(this.entry.time).format('HH:mm');
    },
    fullTime() {
      return moment(this.entry.time).format('D MMM YYYY HH:mm') + (this.entry.saves > 1 ? ' · ' + this.$tc('activity_log.saves', this.entry.saves, { count: this.entry.saves }) : '');
    }
  },
  methods: {
    toggleText(key) {
      const index = this.openTexts.indexOf(key);
      if (index === -1) {
        this.openTexts.push(key);
      } else {
        this.openTexts.splice(index, 1);
      }
    }
  }
};
</script>
